import { createContext, useContext, useEffect, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import axiosInstance from '@libs/axios';
import { apiUrl } from '@constants/url';
import type { ModuleCode } from '@constants/modules';

interface CompanyFeaturesContextType {
  /** featureCode -> enabled. Empty while loading (treated as all-enabled). */
  features: Record<string, boolean>;
  isLoading: boolean;
  /** Fail-open: unknown codes and load errors count as enabled. */
  isFeatureEnabled: (code: ModuleCode | string) => boolean;
}

const CompanyFeaturesContext = createContext<CompanyFeaturesContextType | undefined>(undefined);

async function fetchCompanyFeatures(): Promise<Record<string, boolean>> {
  const response = await axiosInstance.get(apiUrl.companyFeatures.me);
  return response.data?.data?.features ?? {};
}

export function CompanyFeaturesProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['company-features'],
    queryFn: fetchCompanyFeatures,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  // Impersonation swaps authData (and thus the effective company) without a
  // reload — refetch so the locks follow the impersonated PT.
  useEffect(() => {
    const onImpersonationChange = () => {
      queryClient.invalidateQueries({ queryKey: ['company-features'] });
    };
    window.addEventListener('impersonationChanged', onImpersonationChange);
    return () => window.removeEventListener('impersonationChanged', onImpersonationChange);
  }, [queryClient]);

  const features = data ?? {};

  const value: CompanyFeaturesContextType = {
    features,
    isLoading,
    isFeatureEnabled: (code) => features[code] !== false,
  };

  return <CompanyFeaturesContext.Provider value={value}>{children}</CompanyFeaturesContext.Provider>;
}

export function useCompanyFeatures() {
  const context = useContext(CompanyFeaturesContext);
  if (context === undefined) {
    throw new Error('useCompanyFeatures must be used within a CompanyFeaturesProvider');
  }
  return context;
}

export default CompanyFeaturesContext;
